import { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  Heart, MessageCircle, Gift, Bell, Lock, LockOpen, X, Users, Swords,
  Megaphone, Trophy, Sparkles, RefreshCcw, ArrowRightLeft, HandCoins,
  Mail, ExternalLink, Radio,
} from "lucide-react";
import { NOTIFICATIONS } from "@/constants/testIds";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import UserBadge from "@/components/UserBadge";

const ICONS = {
  like: Heart,
  comment: MessageCircle,
  gift: Gift,
  photo_request: Lock,
  photo_granted: LockOpen,
  photo_denied: X,
  cohost_invite: Users,
  cohost_accepted: Users,
  cohost_declined: Users,
  battle_challenge: Swords,
  battle_accepted: Swords,
  battle_champion: Trophy,
  broadcast: Megaphone,
  message: Mail,
  new_follower: Users,
  match_desire: Sparkles,
  trade_proposed: ArrowRightLeft,
  trade_accepted: ArrowRightLeft,
  trade_declined: ArrowRightLeft,
  trade_cancelled: ArrowRightLeft,
  group_invite: Users,
  group_post: MessageCircle,
  nudge: HandCoins,
  conversion_receipt: HandCoins,
  ambassador_approved: Sparkles,
  ambassador_rejected: X,
  ambassador_tier_up: Trophy,
  ambassador_drop: HandCoins,
  community_join_request: Users,
  community_request_approved: Users,
  community_member_joined: Users,
  community_invite: Users,
  prize_ready: Gift,
  live_started: Radio,
};

function timeAgo(iso) {
  if (!iso) return "";
  const s = Math.max(0, (Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60) return `${Math.floor(s)}s`;
  if (s < 3600) return `${Math.floor(s / 60)}m`;
  if (s < 86400) return `${Math.floor(s / 3600)}h`;
  return `${Math.floor(s / 86400)}d`;
}

function verbFor(n) {
  if (n.type === "like") return "liked your post";
  if (n.type === "comment") return "commented on your post";
  if (n.type === "gift") return n.gift_emoji ? `sent you ${n.gift_emoji} (${n.coins || 0} coins)` : `sent you ${n.coins || ""} coins`;
  if (n.type === "photo_request") return "requested access to your private photos";
  if (n.type === "photo_granted") return "granted your photo access";
  if (n.type === "photo_denied") return "declined your photo access";
  if (n.type === "cohost_invite") return "invited you to co-host their live stream";
  if (n.type === "cohost_accepted") return "accepted your co-host invite";
  if (n.type === "cohost_declined") return "declined your co-host invite";
  if (n.type === "battle_challenge") return "challenged you to a battle";
  if (n.type === "battle_accepted") return "accepted your battle challenge";
  if (n.type === "battle_champion") return n.message ? `— ${n.message}` : "was crowned Battle Champion 👑";
  if (n.type === "preferred_name_changed") return `Your preferred name is now “${n.meta?.new || ""}”`;
  if (n.type === "nudge") return n.message ? `nudged you: "${n.message}"` : "nudged you 👋";
  if (n.type === "conversion_receipt") return `Receipt: commission converted — ${n.message || "coins credited"} 🐼`;
  if (n.type === "trade_proposed") return "proposed a gift trade with you 🔄";
  if (n.type === "trade_accepted") return "accepted your gift trade — swap complete! ✨";
  if (n.type === "trade_declined") return "declined your gift trade";
  if (n.type === "group_invite") return "added you to a private group";
  if (n.type === "group_post") return "posted in your group 💬";
  if (n.type === "trade_cancelled") return "cancelled a gift trade with you";
  if (n.type === "new_follower") return "started following you";
  if (n.type === "message") return n.preview ? `DM'd you: "${n.preview}"` : "sent you a message";
  if (n.type === "broadcast") return n.heading ? `📣 ${n.heading}` : (n.subject || "sent an announcement");
  if (n.type === "ambassador_approved") return "🎉 You're a Panda Ambassador!";
  if (n.type === "ambassador_rejected") return n.reason ? `Your ambassador application was declined — ${n.reason}` : "Your ambassador application was declined";
  if (n.type === "ambassador_tier_up") return n.tier ? `You leveled up — ${n.tier.toUpperCase()} Ambassador 🏆` : "Ambassador tier up 🏆";
  if (n.type === "ambassador_drop") return n.coins ? `dropped ${n.coins} bonus coins in your wallet 💰` : "Ambassador coin drop 💰";
  if (n.type === "community_join_request") return "requested to join your community";
  if (n.type === "community_request_approved") return "approved your community join request 🎉";
  if (n.type === "community_member_joined") return "joined your community 👋";
  if (n.type === "community_invite") return "invited you to a private community";
  if (n.type === "prize_ready") return n.message || "Your prize is ready to ship 📦";
  if (n.type === "live_started") {
    return n.stream_title
      ? `is live: "${n.stream_title}" — tap to join 🔴`
      : "just went live — tap to join 🔴";
  }
  if (n.type === "match_desire") {
    const n2 = n.overlap_count || 3;
    return `matches your vibe — ${n2} shared desires ✨`;
  }
  return "did something";
}

export default function NotificationsPage() {
  const [rows, setRows] = useState([]);
  const [requests, setRequests] = useState([]);
  const navigate = useNavigate();

  const load = useCallback(() => {
    api.get("/notifications").then(({ data }) => setRows(data)).catch(() => {});
    api.get("/photos/requests").then(({ data }) => setRequests(data)).catch(() => {});
  }, []);

  useEffect(() => { load(); }, [load]);

  const unreadCount = rows.filter((r) => !r.read).length;

  const markAll = async () => {
    await api.post("/notifications/read-all");
    load();
  };

  const respondCohost = async (n, accept) => {
    if (!n.invite_id) return;
    try {
      const { data } = await api.post(`/live/cohost/invites/${n.invite_id}/respond`, { accept });
      toast.success(accept ? "You're a co-host!" : "Invite declined.");
      if (accept && data?.stream_id) {
        navigate(`/live?s=${data.stream_id}`);
      }
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not respond.");
    }
  };

  const respondBattle = async (n, accept) => {
    if (!n.battle_id) return;
    try {
      await api.post(`/battles/${n.battle_id}/respond`, { accept });
      toast.success(accept ? "Battle on!" : "Challenge declined.");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not respond.");
    }
  };

  const resolveRequest = async (id, action) => {
    try {
      await api.post(`/photos/requests/${id}`, { action });
      toast.success(action === "grant" ? "Access granted." : "Request denied.");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not update.");
    }
  };

  return (
    <div data-testid={NOTIFICATIONS.root} className="p-6 lg:p-8">
      <header className="sticky top-[calc(52px+env(safe-area-inset-top))] lg:top-0 -mx-6 lg:-mx-8 px-6 lg:px-8 py-4 glass border-b border-white/5 mb-6 z-10 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <h1 className="font-display text-2xl tracking-tight">Notifications</h1>
          {unreadCount > 0 && (
            <span
              className="text-[10px] uppercase tracking-widest bg-rose-600 text-white px-2 py-0.5"
              data-testid="notifications-unread-badge"
            >
              {unreadCount} new
            </span>
          )}
        </div>
        <Button
          data-testid={NOTIFICATIONS.markAll}
          onClick={markAll}
          variant="outline"
          className="rounded-none bg-transparent border border-white/15 text-white hover:border-rose-600 text-xs"
        >
          Mark all read
        </Button>
      </header>

      {requests.length > 0 && (
        <div className="mb-6">
          <div className="text-xs uppercase tracking-[0.3em] text-rose-500 mb-3 flex items-center gap-2">
            <Lock size={12} /> Photo access requests
          </div>
          <div className="space-y-2">
            {requests.map((r) => (
              <div
                key={r.id}
                data-testid={`photo-request-${r.id}`}
                className="card-surface p-4 flex items-center gap-3"
              >
                <Avatar className="w-10 h-10 border border-white/10">
                  <AvatarImage src={r.requester.avatar_url} />
                  <AvatarFallback className="bg-rose-600 text-white text-sm">
                    {r.requester.display_name?.[0]?.toUpperCase()}
                  </AvatarFallback>
                </Avatar>
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-medium">
                    {r.requester.display_name} wants to see your private photos
                  </div>
                  <div className="text-xs text-zinc-500 truncate">
                    {r.requester.bio?.trim() || `${timeAgo(r.created_at)} ago`}
                  </div>
                </div>
                <Button
                  size="sm"
                  onClick={() => resolveRequest(r.id, "deny")}
                  data-testid={`photo-request-${r.id}-deny`}
                  className="rounded-none text-xs bg-transparent border border-white/15 text-white hover:border-rose-600"
                >
                  Deny
                </Button>
                <Button
                  size="sm"
                  onClick={() => resolveRequest(r.id, "grant")}
                  data-testid={`photo-request-${r.id}-grant`}
                  className="btn-crimson rounded-none text-xs"
                >
                  Grant
                </Button>
              </div>
            ))}
          </div>
        </div>
      )}

      {rows.length === 0 && requests.length === 0 && (
        <div className="card-surface p-12 text-center">
          <Bell size={28} className="mx-auto text-zinc-600 mb-3" />
          <div className="font-display text-xl">No notifications yet.</div>
          <div className="text-sm text-zinc-500 mt-1">Interact with the vault and they'll show up here.</div>
        </div>
      )}

      <ul className="flex flex-col">
        {rows.map((n) => {
          const Icon = ICONS[n.type] || Bell;
          const showCohost = n.type === "cohost_invite";
          const showBattle = n.type === "battle_challenge";
          const showBroadcast = n.type === "broadcast";
          // System notifications (no actor) — ambassador status changes, prize
          // ready pings, tier ups. Render without an avatar so the row doesn't
          // look like it's from another user.
          const isSystem = !n.actor?.handle && [
            "ambassador_approved", "ambassador_rejected", "ambassador_tier_up",
            "ambassador_drop", "prize_ready", "battle_champion",
          ].includes(n.type);
          const showFollowBack =
            (n.type === "new_follower" || n.type === "match_desire") &&
            n.actor?.handle &&
            n.viewer_follows_actor === false;
          const showJoinLive = n.type === "live_started" && n.stream_id;
          const followBack = async () => {
            try {
              await api.post(`/users/${n.actor.id}/follow`);
              toast.success(`Following ${n.actor.display_name}`);
              load();
            } catch (e) { toast.error(e?.response?.data?.detail || "Could not follow."); }
          };
          return (
            <li
              key={n.id}
              data-testid={NOTIFICATIONS.item(n.id)}
              className={`flex items-start gap-4 p-4 border-b border-white/5 ${
                n.read ? "opacity-70" : "bg-white/[0.02]"
              }`}
            >
              <div className="w-9 h-9 rounded-none bg-rose-600/15 flex items-center justify-center flex-shrink-0">
                <Icon size={16} className="text-rose-500" />
              </div>
              {showBroadcast ? (
                // Broadcast notifs are the platform speaking, not another user —
                // render a bigger card with the heading, body preview, and CTA
                // button so it reads like an inbox message, not a bell ping.
                <div className="flex-1 min-w-0">
                  <div className="text-[10px] uppercase tracking-widest text-rose-500 mb-1">
                    Announcement
                  </div>
                  <div className="font-display text-lg leading-tight">
                    {n.heading || n.subject}
                  </div>
                  {n.body && (
                    <div className="text-sm text-zinc-400 mt-1 line-clamp-3">
                      {n.body}
                    </div>
                  )}
                  <div className="text-xs text-zinc-500 mt-2 flex items-center gap-3">
                    <span>{timeAgo(n.created_at)} ago</span>
                    {n.cta_url && n.cta_label && (
                      <a
                        href={n.cta_url}
                        target={n.cta_url.startsWith("http") ? "_blank" : undefined}
                        rel="noreferrer"
                        data-testid={`notif-cta-${n.id}`}
                        className="btn-crimson rounded-none inline-flex items-center gap-1 px-3 py-1.5 text-xs font-medium"
                      >
                        {n.cta_label}
                        {n.cta_url.startsWith("http") && <ExternalLink size={11} />}
                      </a>
                    )}
                  </div>
                </div>
              ) : isSystem ? (
                // System notifs — no avatar, just the icon + verb. Reads as a
                // Panda X status update rather than a message from another user.
                <div className="flex-1 min-w-0">
                  <div className="text-sm text-zinc-200">{verbFor(n)}</div>
                  <div className="text-xs text-zinc-500 mt-1">{timeAgo(n.created_at)} ago</div>
                </div>
              ) : (
                <>
                  <UserBadge
                    userRef={n.actor?.id || n.actor?.handle}
                    href={n.actor?.handle ? `/u/${n.actor.id}` : undefined}
                    className="flex-shrink-0 focus:outline-none focus-visible:ring-2 focus-visible:ring-rose-400 rounded-full"
                    data-testid={`notif-actor-${n.id}`}
                    aria-label={n.actor?.handle ? `View ${n.actor.display_name}'s profile` : "Actor"}
                  >
                    <Avatar className="w-9 h-9 border border-white/10">
                      <AvatarImage src={n.actor?.avatar_url} />
                      <AvatarFallback className="bg-rose-600 text-white text-sm">
                        {n.actor?.display_name?.[0]?.toUpperCase()}
                      </AvatarFallback>
                    </Avatar>
                  </UserBadge>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm">
                      <UserBadge
                        userRef={n.actor?.id || n.actor?.handle}
                        href={n.actor?.handle ? `/u/${n.actor.id}` : undefined}
                        as="span"
                        className="font-medium cursor-pointer hover:text-rose-400 focus:outline-none focus-visible:ring-2 focus-visible:ring-rose-400 rounded-sm"
                      >
                        {n.actor?.display_name || n.actor?.handle}
                      </UserBadge>{" "}
                      <span className="text-zinc-400">{verbFor(n)}</span>
                    </div>
                    <div className="text-xs text-zinc-500 mt-1">{timeAgo(n.created_at)} ago</div>
                  </div>
                  {(showCohost || showBattle) && (
                    <div className="flex items-center gap-2">
                      <Button
                        size="sm"
                        onClick={() => (showCohost ? respondCohost(n, false) : respondBattle(n, false))}
                        data-testid={`notif-decline-${n.id}`}
                        className="rounded-none text-xs bg-transparent border border-white/15 text-white hover:border-rose-600"
                      >
                        Decline
                      </Button>
                      <Button
                        size="sm"
                        onClick={() => (showCohost ? respondCohost(n, true) : respondBattle(n, true))}
                        data-testid={`notif-accept-${n.id}`}
                        className="btn-crimson rounded-none text-xs"
                      >
                        Accept
                      </Button>
                    </div>
                  )}
                  {showFollowBack && (
                    <Button
                      size="sm"
                      onClick={followBack}
                      data-testid={`notif-followback-${n.id}`}
                      className="btn-crimson rounded-full text-xs h-8 px-3"
                    >
                      Follow back
                    </Button>
                  )}
                  {showJoinLive && (
                    <Button
                      size="sm"
                      onClick={() => navigate(`/live?s=${n.stream_id}`)}
                      data-testid={`notif-join-live-${n.id}`}
                      className="btn-crimson rounded-full text-xs h-8 px-3"
                    >
                      Join live
                    </Button>
                  )}
                </>
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
}
